import { useState } from 'react';
import { authApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeSlash, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ChangePasswordDialog = ({ open, onOpenChange, forced = false }) => {
  const { language } = useLanguage();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  const reset = () => {
    setForm({ current: '', next: '', confirm: '' });
    setShow({ current: false, next: false, confirm: false });
  };

  const submit = async () => {
    if (!form.current || !form.next || !form.confirm) {
      toast.error(t('Tous les champs sont requis', 'All fields required'));
      return;
    }
    if (form.next.length < 8) {
      toast.error(t('Au moins 8 caractères', 'At least 8 characters'));
      return;
    }
    if (form.next !== form.confirm) {
      toast.error(t('La confirmation ne correspond pas', 'Confirmation does not match'));
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword(form.current, form.next);
      toast.success(t('Mot de passe mis à jour', 'Password updated'));
      reset();
      onOpenChange(false);
      // Force a page reload when the change was triggered by the expiry policy,
      // so the freshly-returned `password_expired=false` flag propagates everywhere.
      if (forced) {
        setTimeout(() => window.location.reload(), 400);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Échec', 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const eyeBtn = (key) => (
    <button
      type="button"
      onClick={() => setShow({ ...show, [key]: !show[key] })}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]"
      data-testid={`toggle-pw-${key}`}
      tabIndex={-1}
    >
      {show[key] ? <EyeSlash size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog" onPointerDownOutside={(e) => forced && e.preventDefault()} onEscapeKeyDown={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Lock size={20} className="text-[#0052FF]" weight="duotone" />
            {t('Changer le mot de passe', 'Change password')}
          </DialogTitle>
          <DialogDescription className="font-manrope">
            {forced ? (
              <span className="text-amber-700" data-testid="password-expired-banner">
                ⚠ {t(
                  'Votre mot de passe a expiré (politique de sécurité : 90 jours pour les comptes Admin et Comptable). Veuillez en choisir un nouveau pour continuer.',
                  'Your password has expired (security policy: 90 days for Admin and Accountant accounts). Please choose a new one to continue.'
                )}
              </span>
            ) : (
              t('Pour votre sécurité, choisissez un mot de passe robuste (8 caractères minimum).',
                'For your security, pick a strong password (8 chars min).')
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-manrope">{t('Mot de passe actuel', 'Current password')}</Label>
            <div className="relative">
              <Input
                type={show.current ? 'text' : 'password'}
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                data-testid="current-password-input"
                autoComplete="current-password"
              />
              {eyeBtn('current')}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-manrope">{t('Nouveau mot de passe', 'New password')}</Label>
            <div className="relative">
              <Input
                type={show.next ? 'text' : 'password'}
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                data-testid="new-password-input"
                autoComplete="new-password"
              />
              {eyeBtn('next')}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-manrope">{t('Confirmer le nouveau', 'Confirm new')}</Label>
            <div className="relative">
              <Input
                type={show.confirm ? 'text' : 'password'}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="confirm-password-input"
                autoComplete="new-password"
              />
              {eyeBtn('confirm')}
            </div>
          </div>
        </div>
        <DialogFooter>
          {!forced && (
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-pw-btn">
              {t('Annuler', 'Cancel')}
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#0052FF] hover:bg-[#0040CC]"
            data-testid="submit-pw-btn"
          >
            {saving ? t('Enregistrement…', 'Saving…') : t('Enregistrer', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
